import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DealCard } from "@/components/DealCard";

interface RecommendationsPanelProps {
  products: Array<{
    id: string;
    slug: string;
    title: string;
    subtitle: string | null;
    image: string | null;
    price: number | null;
    rating: number | null;
    categories: any;
    relevance_score: number;
  }> | undefined;
  agentProfile: {
    career_stage: string | null;
    years_in_real_estate: number | null;
    deals_closed_last_year: number | null;
    primary_market: string | null;
    goals: any[] | null;
  } | null | undefined;
}

export function RecommendationsPanel({ products, agentProfile }: RecommendationsPanelProps) {
  const careerStage = agentProfile?.career_stage || 'new agent';
  const dealsClosedLastYear = agentProfile?.deals_closed_last_year || 0;
  const goals = agentProfile?.goals as string[] | null;
  const primaryGoal = goals && goals.length > 0 ? goals[0] : null;

  let profileContext = primaryGoal 
    ? `Because you want to ${primaryGoal.toLowerCase()}, these tools help you get there`
    : `Because you're a ${careerStage}${dealsClosedLastYear > 0 ? ` closing ${dealsClosedLastYear} deals/year` : ''}`;

  if (!products || products.length === 0) {
    return (
      <div className="border rounded-lg p-6 bg-card">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Recommended for You</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {profileContext}
        </p>
        <div className="text-center py-8">
          <Sparkles className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground mb-4">
            Complete your agent profile to get personalized recommendations based on your goals and experience.
          </p>
          <Link to="/ask-circle">
            <Button variant="outline">
              Chat with AskCircle
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-6 bg-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Recommended for You</h2>
        </div>
        <Link to="/browse">
          <Button variant="ghost" size="sm">
            See More
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      </div>
      
      <p className="text-sm text-muted-foreground mb-6">
        {profileContext}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {products.map((product) => (
          <DealCard
            key={product.id}
            id={product.id}
            slug={product.slug}
            title={product.title}
            subtitle={product.subtitle || ''}
            image={product.image || ''}
            price={product.price}
            msrp={null}
            rating={product.rating}
            review_count={0}
            categories={product.categories}
            is_select={false}
            isFavorited={false}
          />
        ))}
      </div>

      <Link to="/browse" className="block mt-6">
        <Button variant="outline" className="w-full">
          Browse All Tools
        </Button>
      </Link>
    </div>
  );
}
